import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import flags from "react-phone-number-input/flags";
import AddRounded from "@mui/icons-material/AddRounded";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CallOutlined from "@mui/icons-material/CallOutlined";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import StarBorderRounded from "@mui/icons-material/StarBorderRounded";
import WhatsApp from "@mui/icons-material/WhatsApp";
import {
  designColors,
  designShell,
  designTheme,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  GlobalFieldLabel,
  globalTextFieldSx,
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
} from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import {
  RequestDetailField,
  detailAccentPalette,
  detailCardSx,
} from "components/DesignSystem/RequestDetailField";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequiredMark } from "components/DesignSystem/SettingsPageShell";
import {
  PanelActionFooter,
  PrimaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import { StatusBadge } from "services/constants";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { contactInitials } from "pages/Administration/SupportContacts/supportContactsData";
import {
  balancedColumnWidth,
  monitoringBodyCellSx,
  monitoringHeadCellSx,
  monitoringNameSx,
  monitoringRowSx,
  useMeasuredHeight,
  useMonitoringListAccess,
} from "pages/Monitoring/listUi";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import {
  mapOrganisationContact,
  organisationContactPayload,
  organisationContactsUrl,
} from "./organisationContactsApi";
import { showToast } from "services/utils/Status";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  CONTACT_TYPES,
  CONTACT_TYPE_COLORS,
  contactToDraft,
  DIAL_CODES,
  emptyContact,
  validateContact,
} from "./organisationContactsRules";

// Standard list headers: drive the table columns, the CustomFilter toolbar
// (search / sort / filter / download) and the "Default View" column picker.
// A row opens its contact in the details panel; there is no Action column.
const contactHeaders = [
  {
    name: "Name",
    key: "name",
    type: "string",
    icon: <PersonOutlineRounded fontSize="small" />,
  },
  {
    name: "Designation",
    key: "designation",
    type: "autocomplete",
    icon: <BadgeOutlined fontSize="small" />,
  },
  {
    name: "Phone Number",
    key: "phone",
    type: "string",
    icon: <CallOutlined fontSize="small" />,
  },
  {
    name: "WhatsApp",
    key: "whatsapp",
    type: "string",
    icon: <WhatsApp fontSize="small" />,
  },
  {
    name: "Email Address",
    key: "email",
    type: "string",
    icon: <MailOutlineRounded fontSize="small" />,
  },
  {
    name: "Contact Type",
    key: "contactType",
    type: "autocomplete",
    icon: <StarBorderRounded fontSize="small" />,
  },
];

// Fixed-layout widths; "Name" takes whatever is left so the table always
// fits its card.
const columnWidths = {
  designation: "16%",
  phone: "14%",
  whatsapp: "14%",
  email: "22%",
  contactType: "13%",
};

// designTheme keeps MUI's default blue primary, so focus rings and selected
// options are set to the app's teal here.
const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: designColors.tealDark,
  },
};
const optionSx = {
  fontSize: 14,
  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
    backgroundColor: designColors.tealTint,
    color: designColors.tealDark,
    fontWeight: 600,
  },
};

const Flag = ({ iso, country }) => {
  const FlagIcon = flags[iso];
  return (
    <Mui.Box
      component="span"
      aria-hidden="true"
      sx={{
        display: "inline-flex",
        width: 22,
        flexShrink: 0,
        borderRadius: "2px",
        overflow: "hidden",
        "& svg": { width: "100%", height: "auto", display: "block" },
      }}
    >
      {FlagIcon ? <FlagIcon title={country} /> : null}
    </Mui.Box>
  );
};

const DialCode = ({ entry, showCountry }) => (
  <Mui.Stack direction="row" spacing={1} alignItems="center">
    <Flag iso={entry.iso} country={entry.country} />
    <span>{entry.code}</span>
    {showCountry && (
      <Mui.Box component="span" sx={{ color: designColors.slate600 }}>
        {entry.country}
      </Mui.Box>
    )}
  </Mui.Stack>
);

// One labelled input of the contact panel.
const PanelField = ({ label, required, error, children }) => (
  <Mui.Box>
    <GlobalFieldLabel>
      {label}
      {required && <RequiredMark />}
    </GlobalFieldLabel>
    {children}
    {error && (
      <Mui.FormHelperText error sx={{ mt: 0.75, fontSize: 12.5 }}>
        {error}
      </Mui.FormHelperText>
    )}
  </Mui.Box>
);

function OrganisationContacts({ permissions }) {
  // Edit permission: add and update. Approver permission: delete.
  const canEdit = permissions?.canEdit !== false;
  const canDelete = permissions?.canDelete !== false;
  const history = useHistory();
  // Guessed backend names; until an "Organisation" module with a "Contacts"
  // submodule is provisioned, saved views stay switched off.
  const { domain, userDetails, subModuleId, canSaveViews } =
    useMonitoringListAccess("Contacts", "Organisation");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // null until CustomFilter computes its first filtered set, so the table
  // shows the rows straight away instead of flashing an empty state.
  const [filteredContacts, setFilteredContacts] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // { mode: "add" | "edit", draft, errors }
  const [panel, setPanel] = useState(null);
  // The contact shown in the read-only details panel. The edit icon in its
  // header swaps it for the form panel; it returns when the form closes.
  const [viewId, setViewId] = useState(null);
  // One expand state for both panels, so Edit opened from a full-screen
  // Contact Details stays full screen. Collapses once every panel is closed.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const togglePanelExpanded = useCallback(
    () => setPanelExpanded((previous) => !previous),
    [],
  );
  const viewOpen = contacts.some((contact) => contact.id === viewId);
  useEffect(() => {
    if (!viewOpen && !panel) setPanelExpanded(false);
  }, [viewOpen, panel]);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [customFilterRef, customFilterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const resetPage = useCallback(() => setPage(0), []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    setContacts([]);
    setFilteredContacts(null);
    setPanel(null);
    setViewId(null);
    setContactToDelete(null);
    setDeleteError("");
    customGetAPI(organisationContactsUrl(domain))
      .then((result) => {
        if (!active) return;
        if (result?.ok && Array.isArray(result.responseDetails)) {
          setContacts(result.responseDetails.map(mapOrganisationContact));
          setPage(0);
        } else
          setLoadError(
            result?.message || "Organisation contacts could not be loaded.",
          );
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        setLoadError(
          error.message || "Organisation contacts could not be loaded.",
        );
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [domain, reloadKey]);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "organisation_contacts_saved_views",
    fullHeaders: contactHeaders,
    screenAccess: canSaveViews,
    onViewChange: resetPage,
  });

  // The filtered set is a snapshot from CustomFilter; map it back to the
  // current records so edits show immediately and deleted rows drop out.
  const visibleContacts = useMemo(() => {
    if (filteredContacts === null) return contacts;
    const byId = new Map(contacts.map((contact) => [contact.id, contact]));
    return filteredContacts
      .map((contact) => byId.get(contact.id))
      .filter(Boolean);
  }, [contacts, filteredContacts]);
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(visibleContacts.length / rowsPerPage) - 1),
  );
  const pageStart = safePage * rowsPerPage;

  const openContact = (contact) =>
    canEdit &&
    setPanel({ mode: "edit", draft: contactToDraft(contact), errors: {} });
  const openAdd = () =>
    canEdit &&
    setPanel({
      mode: "add",
      // The first contact of an organisation has to be the primary one.
      draft: {
        ...emptyContact(),
        contactType: contacts.length ? "Secondary" : "Primary",
      },
      errors: {},
    });

  const setDraft = (key, value) => {
    if (savingRef.current) return;
    setPanel((previous) => ({
      ...previous,
      draft: { ...previous.draft, [key]: value },
      // A dial code change clears the error of the number it belongs to.
      errors: { ...previous.errors, [key.replace("Code", "")]: "" },
    }));
  };

  const saveContact = async () => {
    if (savingRef.current || !panel) return;
    const { mode, draft } = panel;
    const errors = validateContact(draft, contacts);
    if (Object.keys(errors).length) {
      setPanel({ ...panel, errors });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    let saved = false;
    try {
      const result = await customAPI(
        organisationContactsUrl(domain, mode === "edit" ? draft.id : null),
        mode === "edit" ? "PUT" : "POST",
        organisationContactPayload(draft),
      );
      if (!mountedRef.current || !result?.ok) return;
      saved = true;
      setPanel(null);
      showToast(
        "success",
        mode === "edit" ? "Contact updated." : "Contact added.",
      );
      const refreshed = await customGetAPI(organisationContactsUrl(domain));
      if (!mountedRef.current) return;
      if (refreshed?.ok && Array.isArray(refreshed.responseDetails)) {
        setContacts(refreshed.responseDetails.map(mapOrganisationContact));
        setFilteredContacts(null);
        setLoadError("");
        if (mode === "add") setPage(0);
      } else
        setLoadError(
          "Contact saved, but the list could not be refreshed. Please retry.",
        );
    } catch (error) {
      if (mountedRef.current) {
        if (saved)
          setLoadError(
            "Contact saved, but the list could not be refreshed. Please retry.",
          );
        else showToast("error", error.message || "Contact could not be saved.");
      }
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const deleteContact = async () => {
    if (!canDelete || savingRef.current || !contactToDelete) return;
    const deletedId = contactToDelete.id;
    savingRef.current = true;
    setSaving(true);
    setDeleteError("");
    let deleted = false;
    try {
      const result = await customAPI(
        organisationContactsUrl(domain, deletedId),
        "PUT",
        { is_delete: true },
      );
      if (!mountedRef.current) return;
      if (!result?.ok) {
        setDeleteError(
          result?.message || "Contact could not be deleted. Please retry.",
        );
        return;
      }
      deleted = true;
      setContacts((previous) =>
        previous.filter((contact) => contact.id !== deletedId),
      );
      setFilteredContacts(null);
      setViewId((previous) => (previous === deletedId ? null : previous));
      setPanel((previous) =>
        previous?.draft.id === deletedId ? null : previous,
      );
      setContactToDelete(null);
      showToast("success", "Contact deleted.");
      const refreshed = await customGetAPI(organisationContactsUrl(domain));
      if (!mountedRef.current) return;
      if (refreshed?.ok && Array.isArray(refreshed.responseDetails)) {
        setContacts(refreshed.responseDetails.map(mapOrganisationContact));
        setFilteredContacts(null);
        setLoadError("");
      } else
        setLoadError(
          "Contact deleted, but the list could not be refreshed. Please retry.",
        );
    } catch (error) {
      if (mountedRef.current) {
        if (deleted)
          setLoadError(
            "Contact deleted, but the list could not be refreshed. Please retry.",
          );
        else
          setDeleteError(
            error.message || "Contact could not be deleted. Please retry.",
          );
      }
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const renderCell = (key, contact) => {
    switch (key) {
      case "name":
        return (
          <Mui.Typography noWrap sx={monitoringNameSx(viewId === contact.id)}>
            {contact.name}
          </Mui.Typography>
        );
      case "contactType":
        return (
          <StatusBadge
            color={CONTACT_TYPE_COLORS[contact.contactType]}
            label={contact.contactType}
          />
        );
      default:
        return contact[key] || "—";
    }
  };

  const viewing = contacts.find((contact) => contact.id === viewId);
  const viewFields = viewing
    ? [
        {
          icon: BadgeOutlined,
          label: "Designation",
          value: viewing.designation,
          accent: detailAccentPalette.indigo,
        },
        {
          icon: CallOutlined,
          label: "Phone Number",
          value: viewing.phone,
          accent: detailAccentPalette.blue,
        },
        {
          icon: WhatsApp,
          label: "WhatsApp Number",
          value: viewing.whatsapp,
          accent: detailAccentPalette.green,
        },
        {
          icon: MailOutlineRounded,
          label: "Email Address",
          value: viewing.email,
          accent: detailAccentPalette.orange,
        },
        {
          icon: StarBorderRounded,
          label: "Contact Type",
          value: viewing.contactType,
          accent: detailAccentPalette.purple,
        },
      ]
    : [];
  const draft = panel?.draft;
  const errors = panel?.errors || {};
  const isEdit = panel?.mode === "edit";
  const textInput = (key, label, extra = {}) => (
    <Mui.TextField
      fullWidth
      size="small"
      value={draft[key]}
      onChange={(event) => setDraft(key, event.target.value)}
      error={Boolean(errors[key])}
      inputProps={{ "aria-label": label }}
      sx={fieldSx}
      {...extra}
    />
  );
  // Dial code select + number, as one row.
  const phoneInput = (key, label, placeholder) => (
    <Mui.Stack direction="row" spacing={1}>
      <Mui.TextField
        select
        size="small"
        value={draft[key + "Code"]}
        onChange={(event) => setDraft(key + "Code", event.target.value)}
        inputProps={{ "aria-label": label + " country code" }}
        SelectProps={{
          renderValue: (code) => (
            <DialCode entry={DIAL_CODES.find((entry) => entry.code === code)} />
          ),
        }}
        sx={{ ...fieldSx, width: 118, flexShrink: 0 }}
      >
        {DIAL_CODES.map((entry) => (
          <Mui.MenuItem key={entry.iso} value={entry.code} sx={optionSx}>
            <DialCode entry={entry} showCountry />
          </Mui.MenuItem>
        ))}
      </Mui.TextField>
      {textInput(key, label, {
        placeholder,
        type: "tel",
        value: draft[key].replace(/\D/g, ""),
        onChange: (event) =>
          setDraft(key, event.target.value.replace(/\D/g, "")),
        inputProps: {
          "aria-label": label,
          inputMode: "numeric",
          pattern: "[0-9]*",
        },
      })}
    </Mui.Stack>
  );

  if (loading) return <Loader info="Loading..." />;

  return (
    <Mui.ThemeProvider theme={designTheme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
          <Mui.Stack spacing={2}>
            <GlobalBreadcrumb
              trail={[
                {
                  label: "Administration",
                  onClick: () => history.push("/admin/administration/user"),
                },
                {
                  label: "Organisation",
                  onClick: () =>
                    history.push("/admin/administration/organisation/details"),
                },
                { label: "Contacts" },
              ]}
            />
            <Mui.Box>
              <Mui.Typography component="h1" sx={designType.pageTitle}>
                Organisation Contacts
              </Mui.Typography>
              <Mui.Typography sx={designType.pageSubtitle}>
                Manage contact persons for this organisation. Mark one contact
                as the primary contact.
              </Mui.Typography>
            </Mui.Box>
            {loadError && (
              <Mui.Alert
                severity="error"
                action={
                  <Mui.Button
                    disabled={saving}
                    onClick={() => setReloadKey((key) => key + 1)}
                  >
                    Retry
                  </Mui.Button>
                }
              >
                {loadError}
              </Mui.Alert>
            )}

            <Mui.Box
              ref={customFilterRef}
              sx={{
                ...designShell.sectionCard,
                ...sharedStickyCustomFilterBarSx,
              }}
            >
              <CustomFilter
                title="contacts"
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={contacts}
                filteredData={visibleContacts}
                filteredDataReady={filteredContacts !== null}
                onFilteredDataChange={setFilteredContacts}
                screenAccess={true}
                hideCount={true}
                labeledToolbar={true}
                downloadFormats={["excel", "pdf", "csv"]}
                searchPlaceholder="Search contacts by name, designation or email..."
                newButtonComponent={
                  canEdit && (
                    <PrimaryButton
                      startIcon={<AddRounded />}
                      onClick={openAdd}
                      disabled={loading || saving || Boolean(loadError)}
                      sx={{
                        minWidth: 0,
                        minHeight: 40,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      Add Contact
                    </PrimaryButton>
                  )
                }
              />
            </Mui.Box>

            <Mui.Box sx={{ ...designShell.sectionCard, width: "100%" }}>
              <Mui.TableContainer
                sx={{
                  width: "100%",
                  // Visible, not auto: the sticky header needs the page as its
                  // scroll parent, and the fixed-layout table always fits.
                  overflowX: "visible",
                  pb: footerHeight ? footerHeight + "px" : 0,
                }}
              >
                <ApproveTable
                  stickyHeader
                  aria-label="Organisation contacts"
                  sx={{ width: "100%", tableLayout: "fixed" }}
                >
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((header) => (
                        <Mui.TableCell
                          key={header.key}
                          scope="col"
                          title={header.name}
                          sx={{
                            ...monitoringHeadCellSx(customFilterHeight),
                            width: balancedColumnWidth(header.key, visibleHeaders, contactHeaders, columnWidths),
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {visibleContacts.length === 0 ? (
                      <TableEmptyState
                        dataLength={contacts}
                        colSpan={visibleHeaders.length}
                      />
                    ) : (
                      visibleContacts
                        .slice(pageStart, pageStart + rowsPerPage)
                        .map((contact) => (
                          <Mui.TableRow
                            key={contact.id}
                            onClick={() => setViewId(contact.id)}
                            sx={monitoringRowSx(viewId === contact.id)}
                          >
                            {visibleHeaders.map((header) => (
                              <Mui.TableCell
                                key={header.key}
                                title={contact[header.key] || undefined}
                                sx={{
                                  ...monitoringBodyCellSx,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {renderCell(header.key, contact)}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        ))
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
              <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
                {paginationFooter({
                  page: safePage,
                  rowsPerPage,
                  total: visibleContacts.length,
                  itemLabel: "contacts",
                  rowsPerPageOptions,
                  onPageChange: setPage,
                  onRowsPerPageChange: (event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                  },
                })}
              </Mui.Box>
            </Mui.Box>
          </Mui.Stack>
        </Mui.Box>
      </FilterProvider>

      <GlobalSidePanel
        // Steps aside while the form panel is up, and comes back in the same
        // (side or full-screen) layout once that closes.
        open={Boolean(viewing) && !panel}
        expanded={panelExpanded && Boolean(viewing) && !panel}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setViewId(null)}
        title="Contact Details"
        subtitle="Contact person registered for this organisation."
        bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
        headerActions={
          viewing &&
          (canEdit || canDelete) && (
            <>
              {/* App tooltip style, and the same soft icon button the panel
                  uses for its own header icons. */}
              {canEdit && (
                <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                  <Mui.IconButton
                    size="small"
                    aria-label={"Edit " + viewing.name}
                    onClick={() => openContact(viewing)}
                    disabled={saving || Boolean(loadError)}
                    sx={{
                      ...designShell.panelSoftIconButton,
                      color: designColors.slate500,
                    }}
                  >
                    <EditOutlined fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              )}
              {canDelete && (
                <Mui.Tooltip title="Delete" arrow slotProps={tooltipStyleProps}>
                  <span>
                    <Mui.IconButton
                      disabled={saving || Boolean(loadError)}
                      size="small"
                      aria-label={"Delete " + viewing.name}
                      onClick={() => {
                        setDeleteError("");
                        setContactToDelete(viewing);
                      }}
                      sx={{
                        ...designShell.panelSoftIconButton,
                        color: "#DC2626",
                      }}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </Mui.IconButton>
                  </span>
                </Mui.Tooltip>
              )}
            </>
          )
        }
      >
        {viewing && (
          <Mui.Stack spacing={2}>
            <Mui.Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid " + designColors.slate200,
              }}
            >
              <Mui.Avatar
                sx={{
                  width: 44,
                  height: 44,
                  fontSize: 15,
                  fontWeight: 600,
                  bgcolor: designColors.tealTint,
                  color: designColors.tealDark,
                }}
              >
                {contactInitials(viewing.name)}
              </Mui.Avatar>
              <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                <Mui.Typography noWrap sx={designType.panelTitle}>
                  {viewing.name}
                </Mui.Typography>
                <Mui.Typography noWrap sx={designType.helperText}>
                  {viewing.designation}
                </Mui.Typography>
              </Mui.Box>
              <RequestStatusChip
                status={viewing.contactType}
                colorCode={CONTACT_TYPE_COLORS[viewing.contactType]}
              />
            </Mui.Stack>
            <Mui.Box sx={detailCardSx}>
              {viewFields.map((field, index) => (
                <RequestDetailField
                  key={field.label}
                  {...field}
                  isLast={index === viewFields.length - 1}
                />
              ))}
            </Mui.Box>
          </Mui.Stack>
        )}
      </GlobalSidePanel>

      <GlobalSidePanel
        open={Boolean(panel)}
        expanded={panelExpanded && Boolean(panel)}
        onToggleExpand={togglePanelExpanded}
        onClose={() => {
          if (!savingRef.current) setPanel(null);
        }}
        title={isEdit ? "Edit Contact" : "Add Contact"}
        subtitle="Contact person registered for this organisation."
        bodySx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
        footer={
          <PanelActionFooter
            onCancel={() => {
              if (!savingRef.current) setPanel(null);
            }}
            primaryLabel={
              isEdit
                ? saving
                  ? "Updating..."
                  : "Update Contact"
                : saving
                  ? "Saving..."
                  : "Save Contact"
            }
            primaryProps={{ onClick: saveContact, disabled: saving }}
          />
        }
      >
        {panel && (
          <Mui.Stack spacing={2.5}>
            <PanelField label="Name" required error={errors.name}>
              {textInput("name", "Name", { placeholder: "Full name" })}
            </PanelField>
            <PanelField label="Designation" error={errors.designation}>
              {textInput("designation", "Designation", {
                placeholder: "e.g. HR Manager",
              })}
            </PanelField>
            <PanelField label="Phone Number" required error={errors.phone}>
              {phoneInput("phone", "Phone Number", "Enter phone number")}
            </PanelField>
            <PanelField label="WhatsApp Number" error={errors.whatsapp}>
              {phoneInput("whatsapp", "WhatsApp Number", "Enter phone number")}
            </PanelField>
            <PanelField label="Email Address" required error={errors.email}>
              {textInput("email", "Email Address", {
                placeholder: "name@company.com",
              })}
            </PanelField>
            <PanelField
              label="Contact Type"
              required
              error={errors.contactType}
            >
              <Mui.TextField
                select
                fullWidth
                size="small"
                value={draft.contactType}
                onChange={(event) =>
                  setDraft("contactType", event.target.value)
                }
                error={Boolean(errors.contactType)}
                inputProps={{ "aria-label": "Contact Type" }}
                sx={fieldSx}
              >
                {CONTACT_TYPES.map((type) => (
                  <Mui.MenuItem key={type} value={type} sx={optionSx}>
                    {type}
                  </Mui.MenuItem>
                ))}
              </Mui.TextField>
            </PanelField>
          </Mui.Stack>
        )}
      </GlobalSidePanel>

      <GlobalConfirmDialog
        open={Boolean(contactToDelete)}
        title="Delete contact"
        onCancel={() => {
          if (!savingRef.current) {
            setContactToDelete(null);
            setDeleteError("");
          }
        }}
        onConfirm={deleteContact}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={saving}
        loadingLabel="Deleting..."
        danger
      >
        Delete {contactToDelete?.name} from this organisation’s contacts?
        {deleteError && (
          <Mui.Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Mui.Alert>
        )}
      </GlobalConfirmDialog>
    </Mui.ThemeProvider>
  );
}

export default withScreenPermissions(
  OrganisationContacts,
  SCREEN_ACCESS.organisationContacts,
  "Organisation Contacts",
);
